/**
 * CategoriaRepository - Repositorio unificado para Categorías (taxonomía)
 *
 * Implementación compartida entre web (Dexie) y mobile (SQLite).
 * Las categorías agrupan conceptos: globales (empresa_id null, seed) o de la
 * empresa. CRUD directo con eventos de sync como el resto de las tablas.
 */

import { v4 as uuidv4 } from 'uuid';
import { ICategoriaRepository } from '../interfaces/ICategoriaRepository';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter } from '../adapters/IDatabaseAdapter';
import { IProfileRepository } from '../interfaces/IProfileRepository';
import { Categoria } from '../../../types/administracion';

export class CategoriaRepository extends BaseRepository<Categoria, string> implements ICategoriaRepository {

  constructor(db: IDatabaseAdapter, profileRepo?: IProfileRepository) {
    super(db, 'categorias', 'categorias'); // tabla local, tabla Supabase

    this.supportsSoftDelete = false;
    this.autoTimestamp = true;

    if (profileRepo) {
      this.setProfileRepository(profileRepo);
    }
  }

  // ==================== CRUD Base ====================

  async add(data: Omit<Categoria, 'id'>): Promise<string> {
    const id = uuidv4();
    const now = this.getCurrentTimestamp();
    const profile = await this.getCurrentProfile();

    // Las categorías creadas desde el cliente son de la empresa activa;
    // las globales solo se crean por seed del backend.
    const record: Categoria = {
      ...data,
      id,
      created_at: data.created_at ?? now,
      updated_at: now,
      empresa_id: data.empresa_id ?? profile?.empresa_activa_id ?? null,
    };

    await this.db.insert('categorias', record);
    this.emitSync('INSERT', record);

    return id;
  }

  async update(data: Partial<Categoria> & { id: string }): Promise<void> {
    const updatedData = {
      ...data,
      updated_at: this.getCurrentTimestamp(),
    };
    await this.db.update('categorias', data.id, updatedData);
    this.emitSync('UPDATE', updatedData as any);
  }

  async delete(id: string): Promise<void> {
    await this.db.delete('categorias', id);
    this.emitSync('DELETE', id);
  }

  // ==================== Taxonomía (spec 2026-08-07) ====================

  async listarParaEmpresa(empresaId: string): Promise<Categoria[]> {
    const todas = await this.db.getAll<Categoria>('categorias');
    return todas
      .filter(c => c.activo && (c.empresa_id === null || c.empresa_id === empresaId))
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }

  /**
   * TODAS las categorías de la empresa + globales, SIN filtro de activo — para
   * el índice de CLASIFICACIÓN de gastos históricos (KAN-171, spec §12.4): un
   * concepto de categoría desactivada sigue clasificando por su tipo. La
   * visibilidad (listarParaEmpresa) es del picker, no de la clasificación.
   */
  async listarTodasParaClasificacion(empresaId: string): Promise<Categoria[]> {
    const todas = await this.db.getAll<Categoria>('categorias');
    return todas.filter(c => c.empresa_id === null || c.empresa_id === empresaId);
  }
}
